import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import OverlayView from "./OverlayView";
import { stageHeight, REFERENCE_WIDTH } from "@serie3/mymediaplanner/Modules/Social/Domain/overlayRenderer";

const SNAP_THRESHOLD = 0.012;
const NUDGE = 0.005;
const NUDGE_COARSE = 0.05;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/** Snap to a target unless the user holds shift. */
const snap = (value, targets, disabled) => {
  if (disabled) return { value, line: null };
  let best = null;
  let bestDistance = SNAP_THRESHOLD;
  targets.forEach((target) => {
    const distance = Math.abs(value - target.at);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = target;
    }
  });

  return best ? { value: best.at, line: best.line } : { value, line: null };
};

/**
 * The drag surface: the image at its real aspect ratio with the title on top.
 *
 * Positions are fractions of the stage, so dragging at whatever size the modal
 * happens to be still produces a layout that is correct at full resolution.
 * Dragging the body moves it; the right handle changes its width.
 */
const OverlayCanvas = ({
  overlay,
  title,
  src,
  imageSize,
  onChange,
  defaultCss = null,
  maxHeight = 560,
}) => {
  const containerRef = useRef(null);
  const [displayWidth, setDisplayWidth] = useState(0);
  const [guides, setGuides] = useState({ x: null, y: null });
  const [dragging, setDragging] = useState(false);
  const [selected, setSelected] = useState(false);
  const dragState = useRef(null);

  const height = stageHeight(imageSize?.width, imageSize?.height);
  const ratio = height / REFERENCE_WIDTH;

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const measure = () => {
      const available = element.clientWidth;
      if (!available) return;
      let width = available;
      if (width * ratio > maxHeight) {
        width = maxHeight / ratio;
      }
      setDisplayWidth(Math.round(width));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [ratio, maxHeight]);

  const endDrag = useCallback(() => {
    dragState.current = null;
    setDragging(false);
    setGuides({ x: null, y: null });
  }, []);

  const handlePointerMove = useCallback(
    (event) => {
      const state = dragState.current;
      if (!state || !displayWidth) return;

      const displayHeight = displayWidth * ratio;
      const dx = (event.clientX - state.startX) / displayWidth;
      const dy = (event.clientY - state.startY) / displayHeight;
      const noSnap = event.shiftKey;
      const start = state.box;

      if (state.mode === "resize") {
        onChange({ ...start, w: clamp(start.w + dx, 0.05, 1.5) });
        return;
      }

      const snappedX = snap(
        start.x + dx,
        [
          { at: 0, line: 0 },
          { at: 0.5 - start.w / 2, line: 0.5 },
          { at: 1 - start.w, line: 1 },
        ],
        noSnap
      );
      const snappedY = snap(
        start.y + dy,
        [
          { at: 0, line: 0 },
          { at: 0.5, line: 0.5 },
        ],
        noSnap
      );

      setGuides({ x: snappedX.line, y: snappedY.line });
      onChange({
        ...start,
        x: clamp(snappedX.value, -0.5, 1.5),
        y: clamp(snappedY.value, -0.5, 1.5),
      });
    },
    [displayWidth, ratio, onChange]
  );

  useEffect(() => {
    if (!dragging) return undefined;
    // Listening on the window, not the element: the pointer regularly leaves the
    // title box mid-drag and dropping those events makes dragging feel sticky.
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
  }, [dragging, handlePointerMove, endDrag]);

  const startDrag = (event, mode, box) => {
    event.preventDefault();
    event.stopPropagation();
    setSelected(true);
    dragState.current = {
      mode,
      startX: event.clientX,
      startY: event.clientY,
      box: { ...box },
    };
    setDragging(true);
  };

  const handleKeyDown = (event) => {
    const moves = {
      ArrowLeft: { x: -1 },
      ArrowRight: { x: 1 },
      ArrowUp: { y: -1 },
      ArrowDown: { y: 1 },
    };
    const move = moves[event.key];
    if (!move) return;

    event.preventDefault();
    const step = event.shiftKey ? NUDGE_COARSE : NUDGE;
    onChange({
      ...overlay.box,
      x: clamp(overlay.box.x + (move.x || 0) * step, -0.5, 1.5),
      y: clamp(overlay.box.y + (move.y || 0) * step, -0.5, 1.5),
    });
  };

  const displayHeight = displayWidth * ratio;

  return (
    <div ref={containerRef} className="w-100 d-flex justify-content-center">
      {displayWidth > 0 && (
        <div
          role="application"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onPointerDown={() => setSelected(false)}
          style={{
            position: "relative",
            outline: "none",
            boxShadow: "0 2px 14px rgba(0,0,0,0.18)",
            borderRadius: "4px",
            overflow: "hidden",
            touchAction: "none",
          }}
        >
          <OverlayView
            overlay={overlay}
            defaultCss={defaultCss}
            title={title}
            src={src}
            imageSize={imageSize}
            displayWidth={displayWidth}
            ignoreEnabled={true}
            selected={selected}
            onTitlePointerDown={(event) => startDrag(event, "move", overlay.box)}
          />

          {guides.x !== null && (
            <div style={{ position: "absolute", left: `${guides.x * 100}%`, top: 0, bottom: 0, width: "1px", background: "#2f6fed", pointerEvents: "none" }} />
          )}
          {guides.y !== null && (
            <div style={{ position: "absolute", top: `${guides.y * 100}%`, left: 0, right: 0, height: "1px", background: "#2f6fed", pointerEvents: "none" }} />
          )}

          {/* Width handle on the right edge of the title box. */}
          <div
            onPointerDown={(event) => startDrag(event, "resize", overlay.box)}
            title="Drag to change the text width"
            style={{
              position: "absolute",
              left: `${(overlay.box.x + overlay.box.w) * displayWidth}px`,
              top: `${overlay.box.y * displayHeight}px`,
              width: "14px",
              height: "14px",
              marginLeft: "-7px",
              marginTop: "-7px",
              background: "#fff",
              border: "2px solid #2f6fed",
              borderRadius: "2px",
              cursor: "ew-resize",
              touchAction: "none",
            }}
          />
        </div>
      )}
    </div>
  );
};

export default OverlayCanvas;
